import { cn } from "@/lib/utils";
import type { QuarterlyReviewStatus } from "@/types/entities";

interface ReviewStepperProps {
  status: QuarterlyReviewStatus;
}

const STEPS: { key: QuarterlyReviewStatus; label: string }[] = [
  { key: "pending_self_reflection", label: "Self-Reflection" },
  { key: "pending_supervisor_feedback", label: "Supervisor Feedback" },
  { key: "finalized", label: "Finalized" },
];

function getStepIndex(status: QuarterlyReviewStatus): number {
  const idx = STEPS.findIndex((s) => s.key === status);
  return idx >= 0 ? idx : 0;
}

export function ReviewStepper({ status }: ReviewStepperProps) {
  const currentIndex = getStepIndex(status);

  return (
    <div className="flex items-center gap-2">
      {STEPS.map((step, index) => {
        const isCompleted = index < currentIndex;
        const isCurrent = index === currentIndex;

        return (
          <div key={step.key} className="flex items-center gap-2">
            {index > 0 && (
              <div
                className={cn(
                  "h-0.5 w-8",
                  isCompleted
                    ? "bg-[hsl(var(--accent))]"
                    : "bg-[hsl(var(--secondary))]",
                )}
              />
            )}
            <div className="flex items-center gap-1.5">
              <div
                className={cn(
                  "flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium",
                  isCompleted &&
                    "bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]",
                  isCurrent &&
                    "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]",
                  !isCompleted &&
                    !isCurrent &&
                    "bg-[hsl(var(--secondary))] text-[hsl(var(--secondary-foreground))]",
                )}
              >
                {isCompleted ? "\u2713" : index + 1}
              </div>
              <span
                className={cn(
                  "text-xs",
                  isCurrent
                    ? "font-medium text-[hsl(var(--foreground))]"
                    : "text-[hsl(var(--muted-foreground))]",
                )}
              >
                {step.label}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
